import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { theme } from '../styles/theme'
import logoFull from '../assets/logo-full.png'

/**
 * Styled container
 */
const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  background: linear-gradient(160deg, ${theme.colors.background} 0%, ${theme.colors.light} 45%, ${theme.colors.lightHover} 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    filter: blur(0px);
    opacity: 0.35;
    z-index: 0;
  }

  @media (max-width: 768px) {
    &::before,
    &::after {
      width: 280px;
      height: 280px;
    }

    &::before {
      top: -100px;
      right: -100px;
    }

    &::after {
      bottom: -120px;
      left: -120px;
    }
  }

  &::before {
    top: -140px;
    right: -140px;
    background: radial-gradient(circle, ${theme.colors.honeycomb} 0%, transparent 70%);
  }

  &::after {
    bottom: -160px;
    left: -160px;
    background: radial-gradient(circle, ${theme.colors.primaryLight} 0%, transparent 70%);
  }
`

/**
 * Styled card
 */
const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.xxlarge};
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.xl};
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    max-width: 100%;
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.large};
  }
`

/**
 * Styled card header
 */
const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 480px) {
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.md};
  }
`

/**
 * Brand logo
 */
const Logo = styled.img`
  width: 180px;
  height: auto;
  display: block;

  @media (max-width: 480px) {
    width: 140px;
  }
`

/**
 * Styled title
 */
const Title = styled.h1`
  font-size: ${theme.typography.fontSize.xxlarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  text-align: center;

  @media (max-width: 480px) {
    font-size: ${theme.typography.fontSize.xlarge};
  }
`

/**
 * Styled subtitle
 */
const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.small};
  color: ${theme.colors.textSecondary};
  text-align: center;
`

/**
 * Styled form
 */
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

/**
 * Styled input group
 */
const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

/**
 * Styled label
 */
const Label = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

/**
 * Styled input
 */
const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.text};
  transition: ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px rgba(246, 180, 0, 0.15);
    background: ${theme.colors.white};
  }

  &::placeholder {
    color: ${theme.colors.textTertiary};
  }
`

/**
 * Styled button
 */
const Button = styled.button<{ disabled?: boolean; isLoading?: boolean }>`
  margin-top: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  width: 100%;
  background: ${props =>
    props.disabled || props.isLoading
      ? theme.colors.border
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`};
  color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: ${props => (props.disabled || props.isLoading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.normal};
  box-shadow: 0 12px 24px rgba(209, 138, 0, 0.2);

  @media (max-width: 480px) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.small};
  }

  &:hover {
    transform: ${props => (props.disabled || props.isLoading ? 'none' : 'translateY(-1px)')};
    box-shadow: ${props => (props.disabled || props.isLoading ? 'none' : '0 16px 28px rgba(209, 138, 0, 0.28)')};
  }
`

/**
 * Styled error message
 */
const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  font-size: ${theme.typography.fontSize.small};
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
`

const ForgotPasswordLink = styled.button`
  display: inline;
  padding: 0;
  border: none;
  background: none;
  color: ${theme.colors.primaryDark};
  font-size: ${theme.typography.fontSize.small};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  align-self: flex-end;
  transition: ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary};
    text-decoration: underline;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.xs};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border};
  }
`

const DividerText = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const Footer = styled.p`
  text-align: center;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textTertiary};
  margin-top: ${theme.spacing.md};
`

/**
 * Login screen component
 * @returns Login screen
 */
function LoginScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Handles form submission
   * @param e - Form event
   * @returns Promise void
   */
  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')

    if (!email.trim() || !password.trim()) {
      setError(t('auth.fillAllFields'))
      return
    }

    try {
      setIsSubmitting(true)
      await login(email.trim(), password)
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || t('auth.loginFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container>
      <Card>
        <CardHeader>
          <Logo src={logoFull} alt="Hivenetic" />
          <Title>{t('auth.signIn')}</Title>
          <Subtitle>{t('auth.welcomeMessage')}</Subtitle>
        </CardHeader>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('auth.email')}
              autoComplete="email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={t('auth.password')}
              autoComplete="current-password"
              required
            />
          </InputGroup>

          <ForgotPasswordLink type="button" onClick={() => navigate('/forgot-password')}>
            {t('auth.forgotPassword')}
          </ForgotPasswordLink>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" disabled={isSubmitting || loading} isLoading={isSubmitting || loading}>
            {isSubmitting || loading ? t('common.loading') : t('button.signIn.text')}
          </Button>

          <Divider>
            <DividerText>Hivenetic</DividerText>
          </Divider>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <ForgotPasswordLink type="button" onClick={() => navigate('/')}>
            {t('auth.backToHivenetic')}
          </ForgotPasswordLink>
        </div>
        <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '14px', color: theme.colors.textSecondary }}>
          {t('auth.noAccount')}{' '}
          <ForgotPasswordLink type="button" onClick={() => navigate('/register')}>
            {t('auth.createOne')}
          </ForgotPasswordLink>
        </p>
      </Card>
    </Container>
  )
}

export default LoginScreen
